import React, { useState, useEffect, useRef } from 'react';
import { Container, Button, Modal, Form, Row, Col, Dropdown, Alert } from 'react-bootstrap';
import { BsPlusLg, BsPencil, BsTrash, BsArrowRight, BsArrowDown, BsCheck2 } from 'react-icons/bs';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import workflowService from '../../services/workflowService';
import organizationalUnitService from '../../services/OrganizationalUnitService';
import userPositionService from '../../services/UserPositionService';
import Select from 'react-select';
import '../../assets/styles/WorkflowSteps.css';

const WorkflowSteps = ({ workflowId, onClose }) => {
    const [workflow, setWorkflow] = useState(null);
    const [steps, setSteps] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedStep, setSelectedStep] = useState(null);
    const [organizationalUnits, setOrganizationalUnits] = useState([]);
    const [availableWorkflows, setAvailableWorkflows] = useState([]);
    const [unitUsers, setUnitUsers] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        stepType: 'hierarchical', // 'hierarchical', 'unit', 'subworkflow', 'dynamicAssignment'
        organizationalUnitId: '',
        subWorkflowId: null,
        isDynamicAssignment: false,
        isOptional: false,
        allowedActions: '',
        allowAllUsersInUnit: false,
        requireManagersOnly: false,
        approvalType: 'specific', // 'specific', 'allUsers', 'managersOnly'
        stopAtFirstFound: true,
        stepOLAHours: 24,  // Required: each step must have OLA
        escalationTargetOrgUnitId: null,
        allowAutoEscalation: true,
    });
    const [selectedActions, setSelectedActions] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedApprovers, setSelectedApprovers] = useState([]);
    const [showActionsDropdown, setShowActionsDropdown] = useState(false);
    const [isHidingUnitUsers, setIsHidingUnitUsers] = useState(false);
    const [isHidingActions, setIsHidingActions] = useState(false);
    const actionsDropdownRef = useRef(null);
    const [error, setError] = useState('');

    const availableActions = [
        { id: 'approve', label: 'Approve' },
        { id: 'reject', label: 'Reject' },
    ];

    useEffect(() => {
        if (workflowId) {
            loadWorkflow();
            loadOrganizationalUnits();
            loadAvailableWorkflows();
        }
    }, [workflowId]);

    useEffect(() => {
        if (formData.organizationalUnitId) {
            loadUnitUsers(formData.organizationalUnitId);
        } else {
            setUnitUsers([]);
            setSelectedUsers([]);
        }
    }, [formData.organizationalUnitId]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(event.target)) {
                setShowActionsDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (!formData.organizationalUnitId) {
            setIsHidingUnitUsers(true);
            setIsHidingActions(true);
            const timer = setTimeout(() => {
                setIsHidingUnitUsers(false);
                setIsHidingActions(false);
            }, 800); // Match new animation duration
            return () => clearTimeout(timer);
        }
    }, [formData.organizationalUnitId]);

    const loadWorkflow = async () => {
        try {
            const data = await workflowService.getWorkflowById(workflowId);
            setWorkflow(data);
            setSteps(data.steps || []);
        } catch (error) {
            console.error('Error loading workflow:', error);
            toast.error('Failed to load workflow');
        }
    };

    const loadOrganizationalUnits = async () => {
        try {
            const data = await organizationalUnitService.getOrganizationChart();
            setOrganizationalUnits(data);
        } catch (error) {
            console.error('Error loading organizational units:', error);
            toast.error('Failed to load organizational units');
        }
    };

    const loadAvailableWorkflows = async () => {
        try {
            const data = await workflowService.getAllWorkflows();
            // Filter out the current workflow to prevent self-referencing
            setAvailableWorkflows(data.filter(w => w.id !== workflowId));
        } catch (error) {
            console.error('Error loading workflows:', error);
            toast.error('Failed to load workflows');
        }
    };

    const loadUnitUsers = async (unitId) => {
        try {
            const data = await userPositionService.getUserPositionsByUnitId(unitId);
            // Create a map of unique users by userId
            const uniqueUsers = data.reduce((acc, position) => {
                if (!acc[position.userId]) {
                    acc[position.userId] = {
                        value: position.userId,
                        label: position.userFullName,
                        positionId: position.id
                    };
                }
                return acc;
            }, {});
            
            setUnitUsers(Object.values(uniqueUsers));
        } catch (error) {
            console.error('Error loading unit users:', error);
            toast.error('Failed to load unit users');
        }
    };

    const renderUnitOption = (unit, level = 0) => {
        const hasUsers = unit.assignedUsers && unit.assignedUsers.length > 0;
        const userCount = unit.assignedUsers ? unit.assignedUsers.length : 0;
        
        const displayText = hasUsers 
            ? `${unit.name} (${unit.unitTypeName}) - ${userCount} user${userCount > 1 ? 's' : ''}`
            : `${unit.name} (${unit.unitTypeName})`;
        
        return (
            <React.Fragment key={unit.id}>
                <option 
                    value={unit.id}
                    disabled={!hasUsers}
                >
                    {displayText}
                </option>
                {unit.children && unit.children.length > 0 && 
                    unit.children.map(child => renderUnitOption(child, level + 1))}
            </React.Fragment>
        );
    };

    const getSelectedUnitDisplayName = () => {
        if (!formData.organizationalUnitId) return '';
        
        const findUnit = (units, id) => {
            for (let unit of units) {
                if (unit.id.toString() === id.toString()) return unit;
                if (unit.children && unit.children.length > 0) {
                    const found = findUnit(unit.children, id);
                    if (found) return found;
                }
            }
            return null;
        };
        
        const unit = findUnit(organizationalUnits, formData.organizationalUnitId);
        if (unit) {
            const typeIcon = unit.unitType?.icon || '•';
            const userCount = unit.assignedUsers ? unit.assignedUsers.length : 0;
            const hasUsers = unit.assignedUsers && unit.assignedUsers.length > 0;
            return hasUsers 
                ? `${typeIcon} ${unit.name} (${unit.unitTypeName}) - ${userCount} user${userCount > 1 ? 's' : ''}`
                : `${typeIcon} ${unit.name} (${unit.unitTypeName})`;
        }
        return '';
    };

    const handleShowModal = (step = null) => {
        if (step) {
            const actions = step.allowedActions ? step.allowedActions.split(',') : [];
            setSelectedActions(actions);
            
            // Determine stepType based on step properties
            let stepType = 'hierarchical';
            if (step.isDynamicAssignment) {
                stepType = 'dynamicAssignment';
            } else if (step.subWorkflowId) {
                stepType = 'subworkflow';
            } else if (step.organizationalUnitId) {
                stepType = 'unit';
            }
            
            setFormData({
                name: step.name,
                description: step.description,
                stepType: stepType,
                organizationalUnitId: step.organizationalUnitId || '',
                subWorkflowId: step.subWorkflowId || null,
                isDynamicAssignment: step.isDynamicAssignment || false,
                isOptional: step.isOptional,
                timeLimit: step.timeLimit || '',
                allowedActions: step.allowedActions || '',
                allowAllUsersInUnit: step.allowAllUsersInUnit || false,
                requireManagersOnly: step.requireManagersOnly || false,
                approvalType: step.requireManagersOnly ? 'managersOnly' : (step.allowAllUsersInUnit ? 'allUsers' : 'specific'),
                stopAtFirstFound: step.stopAtFirstFound !== undefined ? step.stopAtFirstFound : true,
                stepOLAHours: step.stepOLAHours || 24,  // Required: default to 24 if not set
                escalationTargetOrgUnitId: step.escalationTargetOrgUnitId || null,
                allowAutoEscalation: step.allowAutoEscalation !== undefined ? step.allowAutoEscalation : true,
            });
            setSelectedStep(step);
            
            // Load unit users if there's an organizational unit and approval type is specific
            const approvalType = step.requireManagersOnly ? 'managersOnly' : (step.allowAllUsersInUnit ? 'allUsers' : 'specific');
            if (step.organizationalUnitId && approvalType === 'specific') {
                loadUnitUsers(step.organizationalUnitId).then(() => {
                    // After loading unit users, set the selected users from approvers
                    if (step.approvers && step.approvers.length > 0) {
                        // Create a map of unique approvers by userId
                        const uniqueApprovers = step.approvers.reduce((acc, approver) => {
                            if (!acc[approver.userId]) {
                                acc[approver.userId] = {
                                    value: approver.userId,
                                    label: approver.userFullName
                                };
                            }
                            return acc;
                        }, {});
                        
                        setSelectedUsers(Object.values(uniqueApprovers));
                    } else {
                        setSelectedUsers([]);
                    }
                });
            } else {
                setUnitUsers([]);
                setSelectedUsers([]);
            }
        } else {
            setSelectedActions([]);
            setFormData({
                name: '',
                description: '',
                stepType: 'hierarchical',
                organizationalUnitId: '',
                subWorkflowId: null,
                isDynamicAssignment: false,
                isOptional: false,
                allowedActions: '',
                allowAllUsersInUnit: false,
                requireManagersOnly: false,
                approvalType: 'specific',
                stopAtFirstFound: true,
            });
            setSelectedStep(null);
            setUnitUsers([]);
            setSelectedUsers([]);
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedStep(null);
        setFormData({
            name: '',
            description: '',
            stepType: 'hierarchical',
            organizationalUnitId: '',
            subWorkflowId: null,
            isDynamicAssignment: false,
            isOptional: false,
            allowedActions: '',
            allowAllUsersInUnit: false,
            requireManagersOnly: false,
            approvalType: 'specific',
            stopAtFirstFound: true,
            stepOLAHours: 24,  // Required: reset to default
            escalationTargetOrgUnitId: null,
            allowAutoEscalation: true,
        });
        setSelectedActions([]);
        setSelectedUsers([]);
        setSelectedApprovers([]);
        setError('');
    };

    const handleActionToggle = (actionId) => {
        setSelectedActions(prev => {
            const newActions = prev.includes(actionId)
                ? prev.filter(id => id !== actionId)
                : [...prev, actionId];
            
            // Update form data with the new actions
            setFormData(prev => ({
                ...prev,
                allowedActions: newActions.join(',')
            }));
            
            return newActions;
        });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'allowedActions') {
            setFormData(prev => ({
                ...prev,
                [name]: selectedActions.join(',')
            }));
        } else if (name === 'organizationalUnitId') {
            // Clear sub-workflow when selecting an organizational unit
            setFormData(prev => ({
                ...prev,
                [name]: value,
                subWorkflowId: null
            }));
        } else if (name === 'subWorkflowId') {
            // Clear organizational unit when selecting a sub-workflow
            setFormData(prev => ({
                ...prev,
                [name]: value,
                organizationalUnitId: ''
            }));

        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handleStepTypeChange = (stepType) => {
        setFormData(prev => ({
            ...prev,
            stepType: stepType,
            organizationalUnitId: stepType === 'unit' ? prev.organizationalUnitId : '',
            subWorkflowId: stepType === 'subworkflow' ? prev.subWorkflowId : null,
            isDynamicAssignment: stepType === 'dynamicAssignment',
            requireManagersOnly: stepType === 'hierarchical' ? prev.requireManagersOnly : false,
            approvalType: 'specific'
        }));
        if (stepType !== 'unit') {
            setSelectedUsers([]);
        }
    };

    const validateStepOLASum = () => {
        if (!workflow) return { isValid: true, warning: null };
        
        // Calculate sum of all step OLAs (including current)
        const currentStepOLA = Number(formData.stepOLAHours) || 24;
        const otherStepsOLA = steps
            .filter(step => !selectedStep || step.id !== selectedStep.id)
            .reduce((sum, step) => sum + (Number(step.stepOLAHours) || 24), 0);
        
        const totalOLA = currentStepOLA + otherStepsOLA;
        const workflowSLA = workflow.defaultSLAHours || 72;
        
        if (totalOLA > workflowSLA) {
            return {
                isValid: true,
                warning: `Warning: Sum of step OLAs (${totalOLA}h) exceeds workflow SLA (${workflowSLA}h)`
            };
        }
        
        return { isValid: true, warning: null };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validate required fields
        if (!formData.name.trim()) {
            setError('Name is required');
            return;
        }

        // Validate based on step type
        if (formData.stepType === 'unit') {
            if (!formData.organizationalUnitId) {
                setError('Please select an organizational unit');
                return;
            }
            if (formData.approvalType === 'specific' && selectedUsers.length === 0) {
                setError('Please select at least one user for specific user approval');
                return;
            }
            if (selectedActions.length === 0) {
                setError('Please select at least one allowed action');
                return;
            }
        } else if (formData.stepType === 'subworkflow') {
            if (!formData.subWorkflowId) {
                setError('Please select a sub-workflow');
                return;
            }
        }

        try {
            const stepData = {
                workflowId: Number(workflowId),
                name: formData.name.trim(),
                description: formData.description?.trim() || '',
                stepOrder: selectedStep ? selectedStep.stepOrder : steps.length + 1,
                organizationalUnitId: formData.stepType === 'unit' && formData.organizationalUnitId ? Number(formData.organizationalUnitId) : null,
                subWorkflowId: formData.stepType === 'subworkflow' && formData.subWorkflowId ? Number(formData.subWorkflowId) : null,
                isDynamicAssignment: formData.isDynamicAssignment,
                isOptional: formData.isOptional,

                allowedActions: formData.stepType === 'unit' ? selectedActions.join(',') : '',
                selectedUsers: formData.stepType === 'unit' && formData.approvalType === 'specific' ? selectedUsers.map(user => Number(user.value)) : [],
                allowAllUsersInUnit: formData.stepType === 'unit' ? formData.allowAllUsersInUnit : false,
                requireManagersOnly: formData.requireManagersOnly,
                stopAtFirstFound: formData.stopAtFirstFound,
                stepOLAHours: Number(formData.stepOLAHours) || 24,  // Required: always send a value
                escalationTargetOrgUnitId: formData.escalationTargetOrgUnitId ? Number(formData.escalationTargetOrgUnitId) : null,
                allowAutoEscalation: formData.allowAutoEscalation
            };

            if (selectedStep) {
                // For update, ensure we're sending all fields
                const updateData = {
                    ...stepData,
                    id: selectedStep.id,
                    createdAt: selectedStep.createdAt,
                    updatedAt: new Date().toISOString()
                };
                await workflowService.updateWorkflowStep(Number(selectedStep.id), updateData);
                toast.success('Step updated successfully');
                handleCloseModal();
                await loadWorkflow();
            } else {
                // For create, send the data directly
                await workflowService.createWorkflowStep(stepData);
                toast.success('Step created successfully');
                handleCloseModal();
                await loadWorkflow();
            }
        } catch (error) {
            console.error('Error saving step:', error);
            toast.error(error.message || 'Failed to save step');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this step?')) {
            try {
                // Ensure the ID is a number
                const stepId = parseInt(id, 10);
                if (isNaN(stepId)) {
                    throw new Error('Invalid step ID');
                }
                
                await workflowService.deleteWorkflowStep(stepId);
                toast.success('Step deleted successfully');
                await loadWorkflow(); // Reload the workflow to update the UI
            } catch (error) {
                console.error('Error deleting step:', error);
                
                // Extract error message from different possible response formats
                let errorMessage = 'Failed to delete step';
                
                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response?.data?.error) {
                    errorMessage = error.response.data.error;
                } else if (error.response?.data) {
                    // If data is a string
                    errorMessage = typeof error.response.data === 'string' ? error.response.data : errorMessage;
                } else if (error.message) {
                    errorMessage = error.message;
                }
                
                toast.error(errorMessage);
            }
        }
    };

    const onDragEnd = async (result) => {
        if (!result.destination) return;

        const items = Array.from(steps);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // Update step orders locally
        const updatedItems = items.map((item, index) => ({
            ...item,
            stepOrder: index + 1
        }));

        // Optimistically update UI
        setSteps(updatedItems);

        try {
            // Update the reordered step in the backend
            await workflowService.reorderWorkflowStep(
                Number(reorderedItem.id), 
                result.destination.index + 1
            );
        } catch (error) {
            console.error('Error updating step order:', error);
            toast.error('Failed to update step order');
            // Reload original order on error
            loadWorkflow();
        }
    };

    // Add static step
    const staticStep = {
        id: 'static',
        name: `${workflow?.name || ''} Request`,
        description: 'Initial request submission',
        stepOrder: 0,
        isStatic: true
    };

    return (
        <Container fluid className="p-4">
            <Row className="mb-4">
                <Col>
                    <p className="text-muted">{workflow?.description}</p>
                </Col>
                <Col xs="auto">
                    <Button 
                        variant="primary" 
                        onClick={() => handleShowModal()}
                    >
                        <BsPlusLg className="me-2" />
                        Add Step
                    </Button>
                </Col>
            </Row>

            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="steps" direction={isMobile ? "vertical" : "horizontal"}>
                    {(provided) => (
                        <div
                            className="workflow-steps-container"
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                        >
                            {/* Static Step */}
                            <div className="workflow-step-wrapper">
                                <div className="workflow-step static-step">
                                    <h5 className="step-name">{staticStep.name}</h5>
                                    <p className="step-description">{staticStep.description}</p>
                                </div>
                                {steps.length > 0 && (
                                    <div className="step-connector">
                                        {isMobile ? (
                                            <BsArrowDown size={20} />
                                        ) : (
                                            <BsArrowRight size={20} />
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Dynamic Steps */}
                            {steps.map((step, index) => (
                                <Draggable 
                                    key={step.id} 
                                    draggableId={step.id.toString()} 
                                    index={index}
                                >
                                    {(provided) => (
                                        <div 
                                            className="workflow-step-wrapper"
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                        >
                                            <div className="workflow-step">
                                                <div className="step-header">
                                                    <span className="step-number">{step.stepOrder}</span>
                                                    <div className="step-actions">
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            className="p-0 me-2"
                                                            onClick={() => handleShowModal(step)}
                                                        >
                                                            <BsPencil />
                                                        </Button>
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            className="p-0 text-danger"
                                                            onClick={() => handleDelete(step.id)}
                                                        >
                                                            <BsTrash />
                                                        </Button>
                                                    </div>
                                                </div>
                                                <h5 className="step-name">{step.name}</h5>
                                                {step.description && (
                                                    <p className="step-description">{step.description}</p>
                                                )}
                                                <div className="step-info">
                                                    {step.organizationalUnitName && (
                                                        <div className="info-item">
                                                            <i className="fas fa-building"></i>
                                                            <span>{step.organizationalUnitName}</span>
                                                        </div>
                                                    )}
                                                    <div className="info-item">
                                                        <i className="fas fa-hourglass-split"></i>
                                                        <span>OLA: {step.stepOLAHours || 24}h</span>
                                                    </div>
                                                    {step.isOptional && (
                                                        <div className="info-item">
                                                            <i className="fas fa-check-circle"></i>
                                                            <span>Optional</span>
                                                        </div>
                                                    )}
                                                    {step.allowAllUsersInUnit && (
                                                        <div className="info-item">
                                                            <i className="fas fa-users"></i>
                                                            <span>All Unit Users</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            {index < steps.length - 1 && (
                                                <div className="step-connector">
                                                    {isMobile ? (
                                                        <BsArrowDown size={20} />
                                                    ) : (
                                                        <BsArrowRight size={20} />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            {/* Step Edit/Create Modal */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {selectedStep ? 'Edit Step' : 'New Step'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        {error && (
                            <Alert variant="danger" className="mb-3">
                                {error}
                            </Alert>
                        )}
                        {validateStepOLASum().warning && (
                            <Alert variant="warning" className="mb-3">
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                {validateStepOLASum().warning}
                            </Alert>
                        )}
                        <Form>
                            <Row>
                                <Col md={8}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Form.Group className="mb-3">
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                            
                            {/* Step Type Selection */}
                            <Form.Group className="mb-3">
                                <Form.Label>Step Type</Form.Label>
                                <div>
                                    <Form.Check
                                        type="radio"
                                        name="stepType"
                                        id="hierarchical"
                                        label="Hierarchical Approval"
                                        checked={formData.stepType === 'hierarchical'}
                                        onChange={() => handleStepTypeChange('hierarchical')}
                                    />
                                    <Form.Check
                                        type="radio"
                                        name="stepType"
                                        id="unit"
                                        label="Specific Unit"
                                        checked={formData.stepType === 'unit'}
                                        onChange={() => handleStepTypeChange('unit')}
                                    />
                                    {/* COMMENTED OUT - Sub-workflow option temporarily disabled
                                    <Form.Check
                                        type="radio"
                                        name="stepType"
                                        id="subworkflow"
                                        label="Sub-workflow"
                                        checked={formData.stepType === 'subworkflow'}
                                        onChange={() => handleStepTypeChange('subworkflow')}
                                    />
                                    */}
                                    <Form.Check
                                        type="radio"
                                        name="stepType"
                                        id="dynamicAssignment"
                                        label="Dynamic Assignment"
                                        checked={formData.stepType === 'dynamicAssignment'}
                                        onChange={() => handleStepTypeChange('dynamicAssignment')}
                                    />
                                </div>
                            </Form.Group>

                            {/* Unit Selection - only show when stepType is 'unit' */}
                            {formData.stepType === 'unit' && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Request Approval Unit</Form.Label>
                                    <Form.Select
                                        name="organizationalUnitId"
                                        value={formData.organizationalUnitId}
                                        onChange={handleInputChange}
                                        style={{ maxHeight: '300px' }}
                                    >
                                        <option value="">Select Unit</option>
                                        {organizationalUnits.map(unit => renderUnitOption(unit))}
                                    </Form.Select>
                                </Form.Group>
                            )}

                            {/* Approval Type - only show when stepType is 'unit' and unit is selected */}
                            {formData.stepType === 'unit' && formData.organizationalUnitId && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Approval Type</Form.Label>
                                    <div>
                                        <Form.Check
                                            type="radio"
                                            name="approvalType"
                                            id="specific"
                                            label="Specific Users"
                                            checked={formData.approvalType === 'specific'}
                                            onChange={() => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    approvalType: 'specific',
                                                    allowAllUsersInUnit: false,
                                                    requireManagersOnly: false
                                                }));
                                            }}
                                        />
                                        <Form.Check
                                            type="radio"
                                            name="approvalType"
                                            id="allUsers"
                                            label="All Users in Unit"
                                            checked={formData.approvalType === 'allUsers'}
                                            onChange={() => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    approvalType: 'allUsers',
                                                    allowAllUsersInUnit: true,
                                                    requireManagersOnly: false
                                                }));
                                                setSelectedUsers([]);
                                            }}
                                        />
                                        <Form.Check
                                            type="radio"
                                            name="approvalType"
                                            id="managersOnly"
                                            label="Managers Only"
                                            checked={formData.approvalType === 'managersOnly'}
                                            onChange={() => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    approvalType: 'managersOnly',
                                                    allowAllUsersInUnit: false,
                                                    requireManagersOnly: true
                                                }));
                                                setSelectedUsers([]);
                                            }}
                                        />
                                    </div>
                                </Form.Group>
                            )}
                            {formData.stepType === 'unit' && formData.organizationalUnitId && formData.approvalType === 'specific' && (
                                <Form.Group className={`mb-3 unit-users-group ${isHidingUnitUsers ? 'hiding' : ''}`}>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <Form.Label className="mb-0">Approvers</Form.Label>
                                        <Button 
                                            variant="link" 
                                            size="sm" 
                                            onClick={() => setSelectedUsers(unitUsers)}
                                            className="p-0"
                                        >
                                            Select All
                                        </Button>
                                    </div>
                                    <Select
                                        isMulti
                                        options={unitUsers}
                                        value={selectedUsers}
                                        onChange={(selected) => setSelectedUsers(selected)}
                                        placeholder="Select approvers who can perform actions..."
                                    />
                                </Form.Group>
                            )}
                            {formData.stepType === 'unit' && formData.organizationalUnitId && (
                                <Form.Group className={`mb-3 allowed-actions-group ${isHidingActions ? 'hiding' : ''}`}>
                                    <Form.Label>Allowed Actions</Form.Label>
                                    <div className="position-relative" ref={actionsDropdownRef}>
                                        <Form.Control
                                            readOnly
                                            onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                                            value={selectedActions.length > 0 
                                                ? availableActions
                                                    .filter(action => selectedActions.includes(action.id))
                                                    .map(action => action.label)
                                                    .join(', ')
                                                : 'Select Actions'
                                            }
                                            style={{
                                                cursor: 'pointer',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}
                                        />
                                        {showActionsDropdown && (
                                            <div 
                                                className="position-absolute w-100 bg-white border rounded-2 mt-1"
                                                style={{ 
                                                    zIndex: 1000,
                                                    maxHeight: '200px',
                                                    overflowY: 'auto',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                                                }}
                                            >
                                                {availableActions.map(action => (
                                                    <div 
                                                        key={action.id}
                                                        className="d-flex align-items-center px-3 py-2 cursor-pointer hover-bg-light"
                                                        onClick={() => handleActionToggle(action.id)}
                                                        style={{ cursor: 'pointer' }}
                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                    >
                                                        <Form.Check
                                                            type="checkbox"
                                                            checked={selectedActions.includes(action.id)}
                                                            onChange={() => {}}
                                                            label={action.label}
                                                            className="m-0 w-100"
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </Form.Group>
                            )}
                            
                            {/* Sub-workflow Selection - only show when stepType is 'subworkflow' */}
                            {formData.stepType === 'subworkflow' && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Sub Workflow</Form.Label>
                                    <Form.Select
                                        name="subWorkflowId"
                                        value={formData.subWorkflowId || ''}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select Sub-workflow</option>
                                        {availableWorkflows.map(wf => (
                                            <option key={wf.id} value={wf.id}>
                                                {wf.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            )}
                            
                            {/* Hierarchical Settings - only show when stepType is 'hierarchical' */}
                            {formData.stepType === 'hierarchical' && (
                                <div className="mb-3 p-3 border rounded bg-light">
                                    <h6 className="text-primary mb-3">
                                        <i className="fas fa-sitemap me-2"></i>
                                        Hierarchical Approval Settings
                                    </h6>
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            id="requireManagersOnly"
                                            name="requireManagersOnly"
                                            label="Require managers only"
                                            checked={formData.requireManagersOnly}
                                            onChange={handleInputChange}
                                        />
                                        <Form.Text className="text-muted">
                                            <small>
                                                Only managers at each hierarchy level can approve this step. Non-managers will be skipped.
                                            </small>
                                        </Form.Text>
                                    </Form.Group>
                                    <Form.Group className="mb-0">
                                        <Form.Check
                                            type="checkbox"
                                            id="stopAtFirstFound"
                                            name="stopAtFirstFound"
                                            label="Stop at first found user/manager"
                                            checked={formData.stopAtFirstFound}
                                            onChange={handleInputChange}
                                        />
                                        <Form.Text className="text-muted">
                                            <small>
                                                <strong>✓ Checked:</strong> Stops at the first level where qualifying users are found, then moves to next workflow step<br/>
                                                <strong>✗ Unchecked:</strong> Continues up hierarchy until reaching top level before moving to next step
                                            </small>
                                        </Form.Text>
                                    </Form.Group>
                                </div>
                            )}
                            
                            {/* Optional step - always show at the end after all type-specific settings */}
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="checkbox"
                                    id="isOptional"
                                    name="isOptional"
                                    label="Optional step"
                                    checked={formData.isOptional}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>

                            {/* SLA/OLA Configuration Section */}
                            <div className="mb-3 p-3 border rounded bg-light">
                                <h6 className="text-primary mb-3">
                                    <i className="fas fa-hourglass-split me-2"></i>
                                    SLA/OLA Configuration
                                </h6>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Step OLA (hours) *</Form.Label>
                                            <Form.Control
                                                type="number"
                                                value={formData.stepOLAHours || 24}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    stepOLAHours: e.target.value ? parseInt(e.target.value) : 24
                                                })}
                                                min="1"
                                                max="720"
                                                required
                                            />
                                            <Form.Text className="text-muted">
                                                <small>Operational Level Agreement for this step (1-720 hours, required)</small>
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Escalation Target Unit</Form.Label>
                                            <Form.Select
                                                value={formData.escalationTargetOrgUnitId || ''}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    escalationTargetOrgUnitId: e.target.value ? parseInt(e.target.value) : null
                                                })}
                                            >
                                                <option value="">Select unit (optional)</option>
                                                {organizationalUnits.map(unit => renderUnitOption(unit))}
                                            </Form.Select>
                                            <Form.Text className="text-muted">
                                                <small>Unit to escalate to if SLA/OLA deadline exceeded</small>
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Form.Group className="mb-0">
                                    <Form.Check
                                        type="checkbox"
                                        id="allowAutoEscalation"
                                        checked={formData.allowAutoEscalation}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            allowAutoEscalation: e.target.checked
                                        })}
                                        label="Allow automatic escalation if SLA/OLA deadline is exceeded"
                                    />
                                    <Form.Text className="text-muted">
                                        <small>If enabled and OLA deadline is missed, automatically escalate to target unit</small>
                                    </Form.Text>
                                </Form.Group>
                            </div>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit">
                            {selectedStep ? 'Update' : 'Create'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
};

export default WorkflowSteps; 